import React, { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { FiX, FiUser, FiMail, FiTrash2, FiPlus, FiUsers, FiSearch } from 'react-icons/fi';
import { foldersService } from '../../../services/folders.service';
import styled from 'styled-components';
import { alpha } from '../../../themes/brand';

interface User {
  id: string;
  name: string;
  email: string;
}

interface Permission {
  userId: string;
  userName: string;
  level: 'read' | 'write' | 'admin';
}

interface Folder {
  id: string;
  name: string;
  permissions: Permission[];
}

interface PermissionsModalProps {
  folder: Folder | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({
  folder,
  isOpen,
  onClose,
  onUpdate
}) => {
  // `getLevelColor` mapeia nível de permissão para semântica de risco, e precisa do tema.
  const theme = useTheme();
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Array<{ user: User; level: 'read' | 'write' | 'admin' }>>([]);
  const [loading, setLoading] = useState(false);
  const [addingUsers, setAddingUsers] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAvailableUsers();
    }
  }, [isOpen]);

  const loadAvailableUsers = async () => {
    try {
      setLoading(true);
      const response = await foldersService.getAvailableUsers();
      setAvailableUsers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      showMessage('error', 'Erro ao carregar usuários disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (term: string) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await foldersService.searchUsers(term);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setSearchResults([]);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    searchUsers(value);
  };

  const addUserToSelection = (user: User, level: 'read' | 'write' | 'admin' = 'read') => {
    if (selectedUsers.some(su => su.user.id === user.id)) {
      showMessage('error', 'Usuário já adicionado à lista');
      return;
    }

    // Verificar se usuário já tem permissão na pasta
    if (folder?.permissions.some(p => p.userId === user.id)) {
      showMessage('error', 'Usuário já possui permissão nesta pasta');
      return;
    }

    setSelectedUsers([...selectedUsers, { user, level }]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const removeUserFromSelection = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(su => su.user.id !== userId));
  };

  const updateUserLevel = (userId: string, level: 'read' | 'write' | 'admin') => {
    setSelectedUsers(selectedUsers.map(su => 
      su.user.id === userId ? { ...su, level } : su
    ));
  };

  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) {
      showMessage('error', 'Selecione pelo menos um usuário');
      return;
    }

    try {
      setAddingUsers(true);
      
      const users = selectedUsers.map(su => ({
        userId: su.user.id,
        level: su.level
      }));

      const response = await foldersService.addMultipleUsersToFolder(folder!.id, users);
      
      if (response.warnings && response.warnings.length > 0) {
        showMessage('error', response.warnings.join('\n'));
      } else {
        showMessage('success', 'Usuários adicionados com sucesso!');
      }
      
      setSelectedUsers([]);
      setShowAddForm(false);
      onUpdate();
    } catch (error) {
      console.error('Erro ao adicionar usuários:', error);
      showMessage('error', 'Erro ao adicionar usuários');
    } finally {
      setAddingUsers(false);
    }
  };

  const removeUserPermission = async (userId: string, userName: string) => {
    if (window.confirm(`Deseja remover ${userName} desta pasta?`)) {
      try {
        await foldersService.removeUserFromFolder(folder!.id, userId);
        showMessage('success', 'Usuário removido com sucesso!');
        onUpdate();
      } catch (error) {
        console.error('Erro ao remover usuário:', error);
        showMessage('error', 'Erro ao remover usuário');
      }
    }
  };

  const getLevelText = (level: string) => {
    switch (level) {
      case 'admin': return 'Administrador';
      case 'write': return 'Leitura/Escrita';
      case 'read': return 'Somente Leitura';
      default: return level;
    }
  };

  /*
    Nível de permissão mapeado para semântica de RISCO, não para cores avulsas: admin é o que mais
    pode quebrar (erro), escrita exige atenção (aviso), leitura é segura (sucesso).
    Os valores vinham do Bootstrap — três cores de uma paleta que não é a deste produto.
  */
  const getLevelColor = (level: string) => {
    switch (level) {
      case 'admin': return theme.error;
      case 'write': return theme.warning;
      case 'read': return theme.success;
      default: return theme.placeholderColor;
    }
  };

  if (!isOpen || !folder) return null;

  return (
    <Overlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {FiUsers({ size: 20 })}
                         Gerenciar Permissões - {folder!.name}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            {FiX({ size: 20 })}
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {message && (
            <Message type={message.type}>
              {message.text}
            </Message>
          )}

          {/* Lista atual de permissões */}
          <Section>
            <SectionTitle>Usuários com Acesso ({folder!.permissions.length})</SectionTitle>
            
                         {folder!.permissions.length === 0 ? (
              <EmptyState>
                {FiUser({ size: 32 })}
                <p>Nenhum usuário com permissão ainda</p>
              </EmptyState>
            ) : (
              <PermissionsList>
                                 {folder!.permissions.map((permission) => (
                  <PermissionItem key={permission.userId}>
                    <UserInfo>
                      <UserAvatar>
                        {FiUser({ size: 16 })}
                      </UserAvatar>
                      <div>
                        <UserName>{permission.userName}</UserName>
                        <UserId>ID: {permission.userId}</UserId>
                      </div>
                    </UserInfo>
                    
                    <UserActions>
                      <PermissionBadge level={permission.level}>
                        {getLevelText(permission.level)}
                      </PermissionBadge>
                      
                      {permission.level !== 'admin' && (
                        <RemoveButton 
                          onClick={() => removeUserPermission(permission.userId, permission.userName)}
                          title="Remover usuário"
                        >
                          {FiTrash2({ size: 14 })}
                        </RemoveButton>
                      )}
                    </UserActions>
                  </PermissionItem>
                ))}
              </PermissionsList>
            )}
          </Section>

          {/* Adicionar novos usuários */}
          <Section>
            <SectionHeader>
              <SectionTitle>Adicionar Usuários</SectionTitle>
              <AddButton onClick={() => setShowAddForm(!showAddForm)}>
                {FiPlus({ size: 16 })}
                {showAddForm ? 'Cancelar' : 'Adicionar'}
              </AddButton>
            </SectionHeader>

            {showAddForm && (
              <AddUserForm>
                {/* Buscar usuários */}
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <SearchIcon>{FiSearch({ size: 16 })}</SearchIcon>
                </SearchContainer>

                {/* Resultados da busca */}
                {searchResults.length > 0 && (
                  <SearchResults>
                    {searchResults.map((user) => (
                      <SearchResultItem 
                        key={user.id}
                        onClick={() => addUserToSelection(user)}
                      >
                        <UserInfo>
                          <UserAvatar>
                            {FiUser({ size: 16 })}
                          </UserAvatar>
                          <div>
                            <UserName>{user.name}</UserName>
                            <UserEmail>{user.email}</UserEmail>
                          </div>
                        </UserInfo>
                        <AddIcon>{FiPlus({ size: 16 })}</AddIcon>
                      </SearchResultItem>
                    ))}
                  </SearchResults>
                )}

                {/* Usuários selecionados */}
                {selectedUsers.length > 0 && (
                  <>
                    <SelectedUsersTitle>
                      Usuários Selecionados ({selectedUsers.length})
                    </SelectedUsersTitle>
                    
                    <SelectedUsersList>
                      {selectedUsers.map((selectedUser) => (
                        <SelectedUserItem key={selectedUser.user.id}>
                          <UserInfo>
                            <UserAvatar>
                              {FiUser({ size: 16 })}
                            </UserAvatar>
                            <div>
                              <UserName>{selectedUser.user.name}</UserName>
                              <UserEmail>{selectedUser.user.email}</UserEmail>
                            </div>
                          </UserInfo>
                          
                          <UserActions>
                            <LevelSelect
                              value={selectedUser.level}
                              onChange={(e) => updateUserLevel(selectedUser.user.id, e.target.value as 'read' | 'write' | 'admin')}
                            >
                              <option value="read">Leitura</option>
                              <option value="write">Escrita</option>
                              <option value="admin">Admin</option>
                            </LevelSelect>
                            
                            <RemoveButton 
                              onClick={() => removeUserFromSelection(selectedUser.user.id)}
                            >
                              {FiTrash2({ size: 14 })}
                            </RemoveButton>
                          </UserActions>
                        </SelectedUserItem>
                      ))}
                    </SelectedUsersList>

                    <FormActions>
                      <ConfirmButton 
                        onClick={handleAddUsers}
                        disabled={addingUsers}
                      >
                        {addingUsers ? 'Adicionando...' : `Adicionar ${selectedUsers.length} Usuário(s)`}
                      </ConfirmButton>
                    </FormActions>
                  </>
                )}
              </AddUserForm>
            )}
          </Section>
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: ${props => props.theme.background};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  width: 700px;
  max-width: 90vw;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.background};
`;

const ModalTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${props => props.theme.text};
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.textSecondary};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.background};
    opacity: 0.8;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  max-height: 600px;
  overflow-y: auto;
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  background-color: ${props => props.type === 'error' ? props.theme.errorBackground : props.theme.successBackground};
  color: ${props => props.type === 'error' ? props.theme.error : props.theme.success};
  border: 1px solid ${props => props.type === 'error' ? props.theme.error : props.theme.success};
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: ${props => props.theme.textSecondary};
  text-align: center;

  svg {
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const PermissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PermissionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.primaryAccent};
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UserName = styled.div`
  color: ${props => props.theme.text};
  font-weight: 500;
  font-size: 14px;
`;

const UserId = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  margin-top: 2px;
`;

const UserEmail = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  margin-top: 2px;
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PermissionBadge = styled.span<{ level: string }>`
  background-color: ${props => {
    switch (props.level) {
      case 'admin': return props.theme.error;
      case 'write': return props.theme.warning;
      case 'read': return props.theme.success;
      default: return props.theme.placeholderColor;
    }
  }};
  color: ${({ theme }) => theme.successOn};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background-color: transparent;
  color: ${({ theme }) => theme.error};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.error};
    color: ${({ theme }) => theme.errorOn};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
`;

const AddUserForm = styled.div`
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 20px;
  background-color: ${props => props.theme.background};
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primaryAccent};
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.textSecondary};
`;

const SearchResults = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  margin-bottom: 16px;
`;

const SearchResultItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid ${props => props.theme.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ theme }) => alpha(theme.primary, 0.1)};
  }
`;

const AddIcon = styled.div`
  color: ${({ theme }) => theme.primaryAccent};
`;

const SelectedUsersTitle = styled.h4`
  color: ${props => props.theme.text};
  margin: 20px 0 12px 0;
  font-size: 14px;
  font-weight: 600;
`;

const SelectedUsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const SelectedUserItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: ${({ theme }) => alpha(theme.primary, 0.1)};
  border: 1px solid ${({ theme }) => alpha(theme.primary, 0.3)};
  border-radius: 6px;
`;

const LevelSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 12px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ConfirmButton = styled.button`
  padding: 12px 24px;
  background-color: ${({ theme }) => theme.success};
  color: ${({ theme }) => theme.successOn};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.success};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default PermissionsModal; 